import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Spin, Tooltip, Typography } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import {
  ListingColumnKey,
  ListingColumnPreferenceRow,
} from "../../../constants/listingColumnKeys";
import ListingColumnPreferenceService from "../../../services/ListingColumnPreference/listingColumnPreference.service";
import Notification from "../Notification";
import { NotificationTypes } from "../../../enums/notificationTypes";
import { UserRoleEnum } from "../../../enums/userRole.enum";
import "./tableLayoutPanel.scss";

const COLUMN_LABELS: Record<string, string> = {
  [ListingColumnKey.TAGS]: "Tags",
  [ListingColumnKey.PATIENT_ID]: "Patient ID",
  [ListingColumnKey.PATIENT_NAME]: "Patient Name",
  [ListingColumnKey.PATIENT_AGE]: "Age",
  [ListingColumnKey.PATIENT_GENDER]: "Gender",
  [ListingColumnKey.STUDY]: "Study",
  [ListingColumnKey.REFERRING_DOCTOR]: "Referring Doctor",
  [ListingColumnKey.PATIENT_HISTORY]: "Patient History",
  [ListingColumnKey.SCAN_CENTER]: "Scan Center",
  [ListingColumnKey.STUDY_DATE]: "Study Date",
  [ListingColumnKey.UPLOADED_DATE]: "Uploaded Date",
  [ListingColumnKey.ASSIGNED_DOCTOR]: "Assigned Doctor",
  [ListingColumnKey.REPORTED_DOCTOR]: "Reported Doctor",
  [ListingColumnKey.TIME_ELAPSED]: "Time Elapsed",
  [ListingColumnKey.TAT]: "TAT",
  [ListingColumnKey.REPORTED_TIME]: "Reported Time",
  [ListingColumnKey.STATUS]: "Status",
  [ListingColumnKey.CASE_STATUS]: "Case Status",
  [ListingColumnKey.UPLOAD_STATUS]: "Upload Status",
  [ListingColumnKey.ACTIONS]: "Actions",
};

export interface TableLayoutPanelProps {
  userRole: UserRoleEnum | undefined;
  listingContext: string | null;
  onSaved?: () => void;
  /**
   * Column keys that stay fixed at the start of the table (same order as here).
   * They cannot be dragged, and other columns cannot be moved above them.
   */
  nonReorderableKeys?: readonly string[];
}

function reorderRows(list: ListingColumnPreferenceRow[], from: number, to: number) {
  if (from === to) return list;
  const next = [...list];
  const [removed] = next.splice(from, 1);
  next.splice(to, 0, removed);
  return next;
}

/** Keeps `lockedOrder` keys first (in that order), then remaining rows in their prior order. */
function normalizeRowsWithLockedPrefix(
  rows: ListingColumnPreferenceRow[],
  lockedOrder: readonly string[]
): ListingColumnPreferenceRow[] {
  if (!lockedOrder.length) return rows;
  const byKey = new Map(rows.map((r) => [r.key, r]));
  const locked: ListingColumnPreferenceRow[] = [];
  for (const k of lockedOrder) {
    const r = byKey.get(k);
    if (r) locked.push(r);
  }
  const lockedSet = new Set(lockedOrder);
  const rest = rows.filter((r) => !lockedSet.has(r.key));
  return [...locked, ...rest];
}

function lockedPrefixLength(
  rows: ListingColumnPreferenceRow[],
  lockedOrder: readonly string[]
): number {
  if (!lockedOrder.length) return 0;
  return lockedOrder.filter((k) => rows.some((r) => r.key === k)).length;
}

const DEFAULT_COLUMN_TOOLTIP =
  "This is a default column. It can't be rearranged or disabled.";

/** Normalize locked column order and force default columns always visible. */
function applyNonReorderableColumnRules(
  rows: ListingColumnPreferenceRow[],
  nonReorderableKeys: readonly string[] | undefined
): ListingColumnPreferenceRow[] {
  if (!nonReorderableKeys?.length) return rows;
  const normalized = normalizeRowsWithLockedPrefix(rows, nonReorderableKeys);
  const lockedSet = new Set(nonReorderableKeys);
  return normalized.map((r) =>
    lockedSet.has(r.key) ? { ...r, visible: true } : r
  );
}

export function TableLayoutPanel({
  userRole,
  listingContext,
  onSaved,
  nonReorderableKeys,
}: TableLayoutPanelProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<ListingColumnPreferenceRow[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const lockedPrefixLen = useMemo(
    () =>
      nonReorderableKeys?.length ? lockedPrefixLength(rows, nonReorderableKeys) : 0,
    [rows, nonReorderableKeys]
  );

  const lockedKeySet = useMemo(
    () => new Set(nonReorderableKeys ?? []),
    [nonReorderableKeys]
  );

  const lockedColumnsIntro = useMemo(() => {
    if (!nonReorderableKeys?.length) return "";
    return nonReorderableKeys
      .map((k) => COLUMN_LABELS[k] ?? k)
      .filter(Boolean)
      .join(", ");
  }, [nonReorderableKeys]);

  useEffect(() => {
    if (!userRole || !listingContext) {
      setLoading(false);
      return;
    }
    setLoading(true);
    ListingColumnPreferenceService.fetch(
      userRole,
      listingContext,
      (res) => {
        const next = res.columns.map((c) => ({ ...c }));
        setRows(applyNonReorderableColumnRules(next, nonReorderableKeys));
      },
      () => {
        Notification({ message: "Could not load column settings", type: NotificationTypes.ERROR });
      },
      () => setLoading(false)
    );
  }, [userRole, listingContext, nonReorderableKeys]);

  const toggleVisible = (index: number) => {
    const key = rows[index]?.key;
    if (key && lockedKeySet.has(key)) return;
    const copy = [...rows];
    copy[index] = { ...copy[index], visible: !copy[index].visible };
    setRows(copy);
  };

  const handleSave = () => {
    if (!userRole || !listingContext) return;
    setSaving(true);
    const payload = applyNonReorderableColumnRules(rows, nonReorderableKeys);
    ListingColumnPreferenceService.save(
      userRole,
      listingContext,
      payload,
      (res) => {
        const next = res.columns.map((c) => ({ ...c }));
        setRows(applyNonReorderableColumnRules(next, nonReorderableKeys));
        Notification({ message: "Column layout saved", type: NotificationTypes.SUCCESS });
        onSaved?.();
      },
      () => {
        Notification({ message: "Save failed", type: NotificationTypes.ERROR });
      },
      () => setSaving(false)
    );
  };

  const handleDragStart = (index: number) => (e: React.DragEvent) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragEnd = () => {
    setDragIndex(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (dropIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    const from = dragIndex;
    if (from === null || from === dropIndex) return;
    const L = lockedPrefixLen;
    if (L > 0 && from < L) return;
    let to = dropIndex;
    if (L > 0 && to < L) to = L;
    if (from === to) {
      setDragIndex(null);
      return;
    }
    setRows((prev) => reorderRows(prev, from, to));
    setDragIndex(null);
  };

  if (!listingContext) {
    return (
      <Typography.Text type="secondary">Column settings are not available for this account.</Typography.Text>
    );
  }

  return (
    <div className="table-layout-panel">
      <Typography.Paragraph type="secondary" className="table-layout-panel__intro">
        Drag to reorder columns and use the checkboxes to show or hide them. Resize columns on the cases table to
        save widths. These changes will be applied to your
        account on all devices.
        {lockedColumnsIntro ? (
          <>
            {" "}
            These default columns stay on the left, are always shown, and cannot be reordered or hidden:{" "}
            {lockedColumnsIntro}.
          </>
        ) : null}
      </Typography.Paragraph>
      {loading ? (
        <Spin />
      ) : (
        <>
          <ul className="table-layout-panel__list">
            {rows.map((row, index) => {
              const rowLocked = lockedPrefixLen > 0 && index < lockedPrefixLen;
              const isDefaultLockedColumn = lockedKeySet.has(row.key);
              const checkboxDisabled =
                row.key === ListingColumnKey.ACTIONS || isDefaultLockedColumn;
              const dragHandle = (
                <span
                  className={`table-layout-panel__drag-handle${rowLocked ? " table-layout-panel__drag-handle--locked" : ""}`}
                  draggable={!rowLocked}
                  onDragStart={rowLocked ? undefined : handleDragStart(index)}
                  onDragEnd={handleDragEnd}
                  title={rowLocked ? undefined : "Drag to reorder"}
                  role="button"
                  tabIndex={rowLocked ? -1 : 0}
                  aria-disabled={rowLocked}
                >
                  <MenuOutlined />
                </span>
              );
              const checkbox = (
                <Checkbox
                  checked={isDefaultLockedColumn ? true : row.visible}
                  disabled={checkboxDisabled}
                  onChange={() => toggleVisible(index)}
                >
                  {COLUMN_LABELS[row.key] ?? row.key}
                </Checkbox>
              );
              return (
                <li
                  key={row.key}
                  className={`table-layout-panel__row${dragIndex === index ? " table-layout-panel__row--dragging" : ""}`}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop(index)}
                >
                  {rowLocked ? (
                    <Tooltip title={DEFAULT_COLUMN_TOOLTIP}>
                      <span className="table-layout-panel__tooltip-anchor">{dragHandle}</span>
                    </Tooltip>
                  ) : (
                    dragHandle
                  )}
                  <span className="table-layout-panel__label">
                    {isDefaultLockedColumn ? (
                      <Tooltip title={DEFAULT_COLUMN_TOOLTIP}>
                        <span className="table-layout-panel__tooltip-anchor">{checkbox}</span>
                      </Tooltip>
                    ) : (
                      checkbox
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
          <Button type="primary" onClick={handleSave} loading={saving}>
            Save
          </Button>
        </>
      )}
    </div>
  );
}

export default TableLayoutPanel;
